import { Suspense, lazy, startTransition, useState, useCallback, useEffect, useRef } from 'react'
import type { AnimationType } from '@/types'
import { CanvasWrapper } from '@/components/Canvas/CanvasWrapper'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useCalligraphyState } from '@/hooks/useCalligraphyState'
import { useFontManager } from '@/hooks/useFontManager'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useExport } from '@/hooks/useExport'
import { useHistory } from '@/hooks/useHistory'
import { useV2Runtime } from '@/v2/runtime/useV2Runtime'
import { useProjectAutosave } from '@/v2/persistence/useProjectAutosave'
import { loadDefaultProjectOnce } from '@/services/projectStorage'
import { createDefaultProjectDocument } from '@/v2/project/defaultProjectDocument'
import type { ProjectDocumentV2 } from '@/v2/project/ProjectDocument'
import { DEFAULT_FONT, DEFAULT_FONT_ASSET_PATH } from '@/constants'
import { getRegisteredFontSource } from '@/services/fontStorage'
import styles from './App.module.css'

const ExportModal = lazy(async () => import('@/components/ExportModal/ExportModal').then((module) => ({
  default: module.ExportModal,
})))
const StudioShell = lazy(async () => import('@/components/Studio/StudioShell').then((module) => ({
  default: module.StudioShell,
})))
const FontManager = lazy(async () => import('@/components/FontManager/FontManager').then((module) => ({
  default: module.FontManager,
})))

// Settings that should be tracked in history for undo/redo
interface HistorySnapshot {
  text: string
  density: number
  shape: string
}

function resolveProductionFontSource(fontName: string): string | undefined {
  return fontName === DEFAULT_FONT
    ? DEFAULT_FONT_ASSET_PATH
    : getRegisteredFontSource(fontName) ?? undefined
}

interface EditorAppProps {
  readonly initialProject?: ProjectDocumentV2
  readonly persistenceEnabled: boolean
}

function useReducedMotionPreference(): boolean {
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ))

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => { setReduced(media.matches) }
    update()
    media.addEventListener('change', update)
    return () => { media.removeEventListener('change', update) }
  }, [])

  return reduced
}

function EditorApp({ initialProject, persistenceEnabled }: EditorAppProps) {
  // State management
  const { state, actions } = useCalligraphyState(initialProject, persistenceEnabled)
  const fontManager = useFontManager({
    activeFont: state.activeFont,
    onActiveFontChange: actions.setActiveFont,
  })
  const exportManager = useExport()
  const reducedMotion = useReducedMotionPreference()
  const handleV2PlaybackComplete = useCallback((terminalProgress: number) => {
    actions.setOrderAmount(terminalProgress)
    actions.setPlaying(false)
  }, [actions])
  // Persisted offsets can outlive the layout they were authored on (older
  // text/font/algorithm). The runtime detects the provably-stale case; resetting
  // here clears the compatibility-facade state, and the sync regenerates cleanly.
  const handleStaleLayoutOffsets = useCallback((targets: readonly ('word' | 'letter' | 'dot')[]) => {
    for (const target of targets) {
      if (target === 'word') actions.resetWordOffsets()
      else if (target === 'letter') actions.resetLetterOffsets()
      else actions.resetDotOffsets()
    }
  }, [actions])
  const v2Runtime = useV2Runtime({
    enabled: true,
    legacyState: state,
    ...(initialProject === undefined ? {} : { initialProject }),
    readyToStart: fontManager.isFontReady,
    resolveFontSource: resolveProductionFontSource,
    onPlaybackComplete: handleV2PlaybackComplete,
    onStaleLayoutOffsets: handleStaleLayoutOffsets,
  })
  const projectPersistenceError = useProjectAutosave(v2Runtime, persistenceEnabled)
  const handleTogglePlayback = useCallback(() => {
    if (reducedMotion || v2Runtime === null) return
    const nextPlaying = !v2Runtime.getPlaybackSnapshot().isPlaying
    v2Runtime.setPlaying(nextPlaying)
    if (!nextPlaying) {
      // Checkpoint scheduler-owned progress once; frames never enter React state.
      actions.setOrderAmount(v2Runtime.getPlaybackSnapshot().progress)
    }
    actions.setPlaying(nextPlaying)
  }, [actions, reducedMotion, v2Runtime])

  useEffect(() => {
    if (!reducedMotion || !state.isPlaying) return
    v2Runtime?.setPlaying(false)
    actions.setPlaying(false)
  }, [actions, reducedMotion, state.isPlaying, v2Runtime])

  const handleRerollMovement = useCallback(() => {
    const values = new Uint32Array(1)
    let nextSeed = state.seed
    if (globalThis.crypto?.getRandomValues !== undefined) {
      globalThis.crypto.getRandomValues(values)
      nextSeed = values[0] ?? 0
    } else {
      nextSeed = Math.floor(Math.random() * 0x1_0000_0000) >>> 0
    }
    if (nextSeed === state.seed) nextSeed = (nextSeed + 1) >>> 0
    actions.setSeed(nextSeed)
  }, [actions, state.seed])

  // History for undo/redo (track text changes)
  const {
    state: historyState,
    setState: setHistoryState,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory<HistorySnapshot>({
    text: state.text,
    density: state.density,
    shape: state.shape,
  })

  // Sync history state back to main state when undo/redo happens
  const lastHistoryRef = useRef(historyState)
  useEffect(() => {
    if (lastHistoryRef.current !== historyState) {
      // History changed (undo/redo), apply to main state
      if (lastHistoryRef.current.text !== historyState.text) {
        actions.setText(historyState.text)
      }
      if (lastHistoryRef.current.density !== historyState.density) {
        actions.setDensity(historyState.density)
      }
      if (lastHistoryRef.current.shape !== historyState.shape) {
        actions.setShape(historyState.shape as Parameters<typeof actions.setShape>[0])
      }
      lastHistoryRef.current = historyState
    }
  }, [historyState, actions])

  // Export modal visibility
  const [showExportModal, setShowExportModal] = useState(false)

  // Handle canvas resize
  const handleCanvasResize = useCallback((_width: number, _height: number) => undefined, [])

  // Generate calligraphy
  const handleGenerate = useCallback(() => {
    if (!fontManager.isFontReady) return
    void v2Runtime?.regenerateLayout()
  }, [fontManager.isFontReady, v2Runtime])

  // Handle order amount change from slider (live update)
  const handleOrderAmountChange = useCallback(
    (amount: number) => {
      v2Runtime?.seekPlayback(amount)
      actions.setOrderAmount(amount)
    },
    [actions, v2Runtime]
  )

  // Handle animation type change (live update)
  const handleAnimationTypeChange = useCallback(
    (type: AnimationType) => {
      actions.setAnimationType(type)
    },
    [actions]
  )

  // Wrapped handlers that track changes in history for undo/redo
  const handleTextChange = useCallback(
    (text: string) => {
      actions.setText(text)
      setHistoryState((prev) => ({ ...prev, text }))
    },
    [actions, setHistoryState]
  )

  const handleDensityChange = useCallback(
    (density: number) => {
      actions.setDensity(density)
      setHistoryState((prev) => ({ ...prev, density }))
    },
    [actions, setHistoryState]
  )

  const handleShapeChange = useCallback(
    (shape: Parameters<typeof actions.setShape>[0]) => {
      actions.setShape(shape)
      setHistoryState((prev) => ({ ...prev, shape }))
    },
    [actions, setHistoryState]
  )

  // Handle export
  const handleExport = useCallback(() => {
    startTransition(() => {
      setShowExportModal(true)
    })
    const snapshot = v2Runtime === null || !fontManager.isFontReady
      ? Promise.reject(new Error('Runtime and active font are not ready for export'))
      : v2Runtime.createPortableSnapshot()
    exportManager.generatePortableHtml(snapshot)
  }, [exportManager, fontManager.isFontReady, v2Runtime])

  // Close export modal
  const handleCloseExport = useCallback(() => {
    startTransition(() => {
      setShowExportModal(false)
    })
    exportManager.clearExport()
  }, [exportManager])

  // Keyboard shortcuts (including undo/redo)
  useKeyboardShortcuts({
    onTogglePlay: handleTogglePlayback,
    onGenerate: handleGenerate,
    onUndo: canUndo ? undo : undefined,
    onRedo: canRedo ? redo : undefined,
  })

  // Loading state
  if (!fontManager.isShellReady) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading Farsicles...</p>
      </div>
    )
  }

  return (
    <ErrorBoundary>
    <div className={styles.app}>
      {projectPersistenceError === null ? null : (
        <div className={styles.persistenceError} role="alert">{projectPersistenceError}</div>
      )}
      <Suspense fallback={null}>
        <StudioShell
          text={state.text}
          shape={state.shape}
          density={state.density}
          color={state.color}
          chaosColor={state.chaosColor}
          fadeInChaos={state.fadeInChaos}
          gridMode={state.gridMode}
          colorTransitionStart={state.colorTransitionStart}
          colorTransitionEnd={state.colorTransitionEnd}
          orderAmount={state.orderAmount}
          playbackRuntime={v2Runtime}
          animationType={state.animationType}
          segments={state.segments}
          renderingEnabled={state.renderingEnabled}
          isPlaying={state.isPlaying}
          animationSpeed={state.animationSpeed}
          easingType={state.easingType}
          easingAmount={state.easingAmount}
          playbackDirection={state.playbackDirection}
          holdAtOrder={state.holdAtOrder}
          holdAtChaos={state.holdAtChaos}
          loopMode={state.loopMode}
          fontSettings={state.fontSettings}
          zoom={state.viewport.zoom}
          showBorder={state.showBorder}
          borderIntensity={state.borderIntensity}
          borderColor={state.borderColor}
          borderRadius={state.borderRadius}
          borderOnTop={state.borderOnTop}
          borderScale={state.borderScale}
          showBackground={state.showBackground}
          backgroundColor={state.backgroundColor}
          showGlow={state.showGlow}
          glowIntensity={state.glowIntensity}
          glowColor={state.glowColor}
          glowSoftness={state.glowSoftness}
          particleScale={state.particleScale}
          wiggleEnabled={state.wiggleEnabled}
          wiggleSpeed={state.wiggleSpeed}
          wiggleForce={state.wiggleForce}
          wiggleDampening={state.wiggleDampening}
          rotationEnabled={state.rotationEnabled}
          rotationSpeed={state.rotationSpeed}
          motionBlur={state.motionBlur}
          reducedMotion={reducedMotion}
          canvasSize={state.canvasSize}
          customSvgData={state.customSvgData}
          customEasingCurve={state.customEasingCurve}
          letterMode={state.letterMode}
          dotMode={state.dotMode}
          rendererType={state.rendererType}
          onTextChange={handleTextChange}
          onShapeChange={handleShapeChange}
          onDensityChange={handleDensityChange}
          onColorChange={actions.setColor}
          onChaosColorChange={actions.setChaosColor}
          onFadeInChaosChange={actions.setFadeInChaos}
          onGridModeChange={actions.setGridMode}
          onColorTransitionStartChange={actions.setColorTransitionStart}
          onColorTransitionEndChange={actions.setColorTransitionEnd}
          onOrderAmountChange={handleOrderAmountChange}
          onAnimationTypeChange={handleAnimationTypeChange}
          onSegmentsChange={actions.setSegments}
          onRenderingEnabledChange={actions.setRenderingEnabled}
          onAnimationSpeedChange={actions.setAnimationSpeed}
          onEasingTypeChange={actions.setEasingType}
          onEasingAmountChange={actions.setEasingAmount}
          onPlaybackDirectionChange={actions.setPlaybackDirection}
          onHoldAtOrderChange={actions.setHoldAtOrder}
          onHoldAtChaosChange={actions.setHoldAtChaos}
          onLoopModeChange={actions.setLoopMode}
          onFontSettingsChange={actions.setFontSettings}
          onZoomChange={(zoom) => actions.setViewport({ zoom })}
          onResetViewport={actions.resetViewport}
          onShowBorderChange={actions.setShowBorder}
          onBorderIntensityChange={actions.setBorderIntensity}
          onBorderColorChange={actions.setBorderColor}
          onBorderRadiusChange={actions.setBorderRadius}
          onBorderOnTopChange={actions.setBorderOnTop}
          onBorderScaleChange={actions.setBorderScale}
          onShowBackgroundChange={actions.setShowBackground}
          onBackgroundColorChange={actions.setBackgroundColor}
          onShowGlowChange={actions.setShowGlow}
          onGlowIntensityChange={actions.setGlowIntensity}
          onGlowColorChange={actions.setGlowColor}
          onGlowSoftnessChange={actions.setGlowSoftness}
          onParticleScaleChange={actions.setParticleScale}
          onWiggleEnabledChange={actions.setWiggleEnabled}
          onWiggleSpeedChange={actions.setWiggleSpeed}
          onWiggleForceChange={actions.setWiggleForce}
          onWiggleDampeningChange={actions.setWiggleDampening}
          onRotationEnabledChange={actions.setRotationEnabled}
          onRotationSpeedChange={actions.setRotationSpeed}
          onMotionBlurChange={actions.setMotionBlur}
          onRerollMovement={handleRerollMovement}
          onCanvasSizeChange={actions.setCanvasSize}
          onCanvasSizePresetChange={actions.setCanvasSizePreset}
          onCustomSvgDataChange={actions.setCustomSvgData}
          onCustomEasingCurveChange={actions.setCustomEasingCurve}
          onLetterModeChange={actions.setLetterMode}
          onDotModeChange={actions.setDotMode}
          onRendererTypeChange={actions.setRendererType}
          onGenerate={handleGenerate}
          onTogglePlay={handleTogglePlayback}
          onExport={handleExport}
          wordInfo={state.wordInfo}
          wordOffsets={state.wordOffsets}
          selectedWordIndex={state.selectedWordIndex}
          onWordOffsetChange={actions.setWordOffset}
          onSelectWord={actions.setSelectedWordIndex}
          onResetWordOffsets={actions.resetWordOffsets}
          letterInfo={state.letterInfo}
          letterOffsets={state.letterOffsets}
          selectedLetterIndex={state.selectedLetterIndex}
          onLetterOffsetChange={actions.setLetterOffset}
          onSelectLetter={actions.setSelectedLetterIndex}
          onResetLetterOffsets={actions.resetLetterOffsets}
          fontManagerContent={
            <Suspense fallback={null}>
              <FontManager
                fonts={fontManager.fonts}
                activeFont={fontManager.activeFont}
                isLoading={fontManager.isLoading}
                error={fontManager.error}
                onAddFont={(file) => {
                  void fontManager.addFont(file)
                }}
                onSelectFont={fontManager.setActiveFont}
                onDeleteFont={(name) => {
                  void fontManager.removeFont(name)
                }}
              />
            </Suspense>
          }
        >
          <CanvasWrapper
            v2Runtime={v2Runtime}
            renderingEnabled={state.renderingEnabled}
            onResize={handleCanvasResize}
            onViewportChange={actions.setViewport}
            onWordDrag={actions.setWordOffset}
            onWordDragBatch={actions.setWordOffsetsBatch}
            onWordDragEnd={handleGenerate}
            onSelectWord={actions.setSelectedWordIndex}
            onLetterDrag={actions.setLetterOffset}
            onLetterDragBatch={actions.setLetterOffsetsBatch}
            onLetterDragEnd={handleGenerate}
            onSelectLetter={actions.setSelectedLetterIndex}
            onDotDrag={actions.setDotOffset}
            onDotDragEnd={handleGenerate}
            onSelectDot={actions.setSelectedDotIndex}
          />
        </StudioShell>
      </Suspense>

      <Suspense fallback={null}>
        {showExportModal ? (
          <ExportModal
            isOpen={showExportModal}
            html={exportManager.exportedHtml}
            isLoading={exportManager.isExporting}
            error={exportManager.error}
            onClose={handleCloseExport}
            onCopy={() => exportManager.copyToClipboard()}
            onDownload={() => exportManager.downloadHtml()}
          />
        ) : null}
      </Suspense>
    </div>
    </ErrorBoundary>
  )
}

interface V2GateState {
  readonly document: ProjectDocumentV2
  readonly persistenceEnabled: boolean
}

function V2ProjectGate() {
  const [state, setState] = useState<V2GateState | null>(null)

  useEffect(() => {
    let active = true
    void loadDefaultProjectOnce().then(
      (loaded) => {
        if (!active) return
        const persistenceEnabled = loaded.source !== 'corrupt' && loaded.source !== 'unsupported'
        setState({ document: loaded.document, persistenceEnabled })
      },
      (error: unknown) => {
        if (!active) return
        console.error('Failed to hydrate the V2 project; using an unsaved default session:', error)
        setState({ document: createDefaultProjectDocument(), persistenceEnabled: false })
      },
    )
    return () => { active = false }
  }, [])

  if (state === null) {
    return (
      <div className={styles.loading} data-v2-project-status="loading">
        <div className={styles.spinner} />
        <p>Loading Farsicles...</p>
      </div>
    )
  }

  return (
    <EditorApp
      initialProject={state.document}
      persistenceEnabled={state.persistenceEnabled}
    />
  )
}

function App() {
  return <V2ProjectGate />
}

export default App
